import { isEmpty } from '@ember/utils';
import type Page from '@linc-technologies/linced-common/models/posts/page';
import type Post from '@linc-technologies/linced-common/models/posts/post';
import type Tag from '@linc-technologies/linced-common/models/posts/tag';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';

// A saved tag: these helpers use its id as a count key.
type TagRecord = Tag & { id: string };

// The parts of a post that getTagCountsForPage reads. Its tags are saved.
type TagPost = Pick<Post, 'date'> & {
  tags: { forEach(callback: (tag: TagRecord) => void): void };
};

export interface TagYearCount {
  year: number;
  count: number;
}

export interface TagCount {
  tagId: string;
  label: string;
  years: TagYearCount[];
}

export interface TagCategoryCount {
  categoryId: string | undefined;
  title: string | undefined;
  tags: TagCount[];
}

export interface TagCounts {
  years: number[];
  data: TagCategoryCount[];
}

export interface TagPieDatum {
  value: string;
  label: string;
  count: number;
  tooltip: string;
}

export function getTagCountsForPage(
  // The page that owns the tag categories.
  page: Pick<Page, 'tagCategories'>,
  posts: readonly TagPost[] | null,
): TagCounts | [] {
  if (isEmpty(page.tagCategories) || !posts || isEmpty(posts)) {
    return [];
  }

  const tagYears: number[] = [];
  const data = posts.reduce<TagCategoryCount[]>((counts, post) => {
    // date falls back to createTime. A saved post has a createTime.
    const year = moment.unix(post.date!).year();
    if (tagYears.indexOf(year) === -1) {
      tagYears.push(year);
    }

    post.tags.forEach((tag) => {
      const categoryId = tag.get('category.id') as string | undefined;
      let category = findBy(counts, 'categoryId', categoryId);
      if (!category) {
        category = {
          categoryId,
          title: tag.get('category.label') as string | undefined,
          tags: [],
        };
        counts.push(category);
      }

      let tagCounts = findBy(category.tags, 'tagId', tag.id);
      if (!tagCounts) {
        tagCounts = {
          tagId: tag.id,
          label: tag.label,
          years: [],
        };
        category.tags.push(tagCounts);
      }

      const yearCount = findBy(tagCounts.years, 'year', year);
      if (!yearCount) {
        tagCounts.years.push({
          year,
          count: 1,
        });
      } else {
        yearCount.count++;
      }
    });

    return counts;
  }, []);

  const minYear = Math.min(...tagYears);
  const maxYear = Math.max(...tagYears);
  const years: number[] = [];
  for (let year = maxYear; year >= minYear; year--) {
    years.push(year);
  }

  return {
    years,
    data,
  };
}

export function getTagPieData(
  counts: readonly TagCount[],
  year: number,
): TagPieDatum[] {
  const data: TagPieDatum[] = [];
  counts.forEach((tagData) => {
    const yearData = findBy(tagData.years, 'year', year);
    if (yearData) {
      data.push({
        value: tagData.tagId,
        label: tagData.label,
        count: yearData.count,
        tooltip: `${tagData.label}: ${yearData.count}`,
      });
    }
  });

  return data;
}

export function sortTags<T extends TagRecord>(
  tags: T[],
  categoryIds: readonly string[],
): T[] {
  return tags.sort((tagA, tagB) => {
    // A tag without a category gets index -1.
    const indexA = categoryIds.indexOf(tagA.get('category.id') as string);
    const indexB = categoryIds.indexOf(tagB.get('category.id') as string);
    const categorySort = indexA - indexB;
    if (categorySort === 0) {
      // Same category - sort by sequence.
      const sequenceSort = tagA.sequence - tagB.sequence;
      if (sequenceSort === 0) {
        // Same sequence too - sort by label.
        switch (true) {
          case tagA.label === tagB.label:
            return 0;
          case tagA.label < tagB.label:
            return -1;
          default:
            return 1;
        }
      }
      return sequenceSort;
    }
    return categorySort;
  });
}
